// SCR-STAFF-314 — تعطیلی گروهی (US-SCH-123). ساعت خالی یعنی کل روز. ثبت بدون تأیید اثر PHX-SCH-0104 برمی‌گرداند.
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type BulkClosureRequest } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { BusinessDateField, ErrorState, FormDialog, LoadingState, PageHeader } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { BranchRequired, IRANIAN_WEEK, useActiveBranch, useBookingOptions } from './common';

export const SCR_STAFF_314 = 'SCR-STAFF-314';

interface Draft {
  reason: string;
  from: string;
  to: string;
  start: string;
  end: string;
  weekdays: number[];
  providerIds: string[];
  departmentIds: string[];
}

const emptyDraft = (): Draft => ({
  reason: '',
  from: '',
  to: '',
  start: '',
  end: '',
  weekdays: [],
  providerIds: [],
  departmentIds: [],
});

function toRequest(branchId: string, draft: Draft, confirmImpact: boolean): BulkClosureRequest {
  return {
    branchId,
    reason: draft.reason || null,
    from: draft.from,
    to: draft.to,
    start: draft.start ? `${draft.start}:00` : null,
    end: draft.end ? `${draft.end}:00` : null,
    weekdays: draft.weekdays,
    providerIds: draft.providerIds,
    departmentIds: draft.departmentIds,
    confirmImpact,
  };
}

export function BulkClosuresPage() {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const { branchId } = useActiveBranch();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [selected, setSelected] = useState<string[]>([]);
  const [impact, setImpact] = useState<number | null>(null);

  const list = useQuery({
    queryKey: ['scheduling', session?.tenantId, 'bulk-closures', branchId],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/scheduling/bulk-closures', {
          params: { query: { branchId: branchId ?? undefined } },
        }),
      ),
    enabled: branchId !== null,
  });
  const providers = useBookingOptions(branchId, null, open);
  const departments = useQuery({
    queryKey: ['org', session?.tenantId, 'departments', branchId],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/organization/departments', {
          params: { query: { branchId: branchId ?? undefined } },
        }),
      ),
    enabled: open && branchId !== null && access.can('org.resource.view'),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['scheduling', session?.tenantId, 'bulk-closures'] });
  const preview = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.POST('/api/v1/scheduling/bulk-closures:impact', { body: toRequest(branchId ?? '', draft, false) })),
    onSuccess: (data) => setImpact(data.count),
  });
  const create = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/bulk-closures', {
          body: toRequest(branchId ?? '', draft, true),
          params: { header: { 'Idempotency-Key': globalThis.crypto.randomUUID() } },
        }),
      ),
    onSuccess: () => {
      setOpen(false);
      setImpact(null);
      void invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/bulk-closures:bulk-delete', {
          body: { ids: selected },
          params: { header: { 'Idempotency-Key': globalThis.crypto.randomUUID() } },
        }),
      ),
    onSuccess: () => {
      setSelected([]);
      void invalidate();
    },
  });

  const rows = list.data?.items ?? [];
  const allSelected = rows.length > 0 && selected.length === rows.length;

  return (
    <Stack data-screen-id={SCR_STAFF_314} spacing={2}>
      <PageHeader
        titleKey="sch:bulk.title"
        actions={
          <Button variant="contained" disabled={branchId === null} onClick={() => { setDraft(emptyDraft()); setImpact(null); setOpen(true); }} data-testid="bulk-create">
            {t('sch:bulk.create')}
          </Button>
        }
      />
      {branchId === null ? (
        <BranchRequired />
      ) : list.isPending ? (
        <LoadingState rows={4} />
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <Stack spacing={1} data-testid="bulk-list">
          <Stack direction="row" spacing={1}>
            <FormControlLabel
              control={<Checkbox checked={allSelected} onChange={() => setSelected(allSelected ? [] : rows.map((row) => row.id))} />}
              label={t('sch:bulk.select_all')}
            />
            <Button variant="outlined" color="error" disabled={selected.length === 0 || remove.isPending} onClick={() => remove.mutate()} data-testid="bulk-delete">
              {t('sch:bulk.delete')}
            </Button>
          </Stack>
          {rows.map((row) => (
            <FormControlLabel
              key={row.id}
              data-testid="bulk-row"
              control={
                <Checkbox
                  checked={selected.includes(row.id)}
                  onChange={() => setSelected((current) => (current.includes(row.id) ? current.filter((id) => id !== row.id) : [...current, row.id]))}
                />
              }
              label={`${row.reason ?? t('sch:bulk.untitled')} · ${row.from} – ${row.to}${row.start ? ` · ${row.start}–${row.end ?? ''}` : ''}`}
            />
          ))}
        </Stack>
      )}
      <FormDialog
        open={open}
        titleKey="sch:bulk.create"
        onClose={() => setOpen(false)}
        onSubmit={() => create.mutate()}
        isDirty={draft.from !== '' || draft.to !== '' || draft.reason !== ''}
        isSubmitting={create.isPending}
        submitError={create.error ?? preview.error}
      >
        <Stack spacing={2}>
          <TextField label={t('sch:bulk.reason')} value={draft.reason} onChange={(event) => setDraft({ ...draft, reason: event.target.value })} />
          <Stack direction="row" spacing={2}>
            <BusinessDateField label={t('sch:bulk.from')} value={draft.from} onChange={(from) => setDraft({ ...draft, from })} testId="bulk-from" />
            <BusinessDateField label={t('sch:bulk.to')} value={draft.to} onChange={(to) => setDraft({ ...draft, to })} testId="bulk-to" />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label={t('sch:bulk.start')} value={draft.start} placeholder="16:00" onChange={(event) => setDraft({ ...draft, start: event.target.value })} inputProps={{ 'data-testid': 'bulk-start' }} />
            <TextField label={t('sch:bulk.end')} value={draft.end} placeholder="20:00" onChange={(event) => setDraft({ ...draft, end: event.target.value })} inputProps={{ 'data-testid': 'bulk-end' }} />
          </Stack>
          <Stack direction="row" sx={{ flexWrap: 'wrap' }}>
            {IRANIAN_WEEK.map((day) => (
              <FormControlLabel
                key={day}
                control={
                  <Checkbox
                    checked={draft.weekdays.includes(day)}
                    onChange={() =>
                      setDraft({
                        ...draft,
                        weekdays: draft.weekdays.includes(day) ? draft.weekdays.filter((item) => item !== day) : [...draft.weekdays, day],
                      })
                    }
                  />
                }
                label={t(`sch:weekday.${day}`)}
              />
            ))}
          </Stack>
          <TextField
            select
            SelectProps={{ multiple: true }}
            label={t('sch:bulk.providers')}
            value={draft.providerIds}
            onChange={(event) => setDraft({ ...draft, providerIds: event.target.value as unknown as string[] })}
          >
            {(providers.data?.providers ?? []).map((provider) => (
              <MenuItem key={provider.id} value={provider.id}>
                {provider.name ?? ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            SelectProps={{ multiple: true }}
            label={t('sch:bulk.departments')}
            value={draft.departmentIds}
            onChange={(event) => setDraft({ ...draft, departmentIds: event.target.value as unknown as string[] })}
          >
            {(departments.data?.items ?? []).map((department) => (
              <MenuItem key={department.id} value={department.id}>
                {department.name ?? ''}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" onClick={() => preview.mutate()} disabled={preview.isPending || draft.from === '' || draft.to === ''} data-testid="bulk-impact">
            {impact === null ? t('sch:bulk.impact') : t('sch:bulk.impact_count', { count: impact })}
          </Button>
        </Stack>
      </FormDialog>
    </Stack>
  );
}
